/* eslint-disable @typescript-eslint/no-explicit-any */
import { NavigationService } from '@revenuewell/logic-navigation';
import { OidcService } from '@revenuewell/logic-oidc';
import stringify from 'json-stable-stringify';
import { createCache, SimpleLocalStorageCache } from "simple-localstorage-cache";
import { ResponseError } from '../../types/response-error';
import { extractErrorFromResponse } from '../../utils/api-response/api-response';
import { incrementCacheUsageStats } from '../../utils/cache-usages-stats';
import { createRequestHash } from '../../utils/create-request-hash';
import { AuthApiServiceError } from './types/auth-api-service-error';

const DEFAULT_CACHE_TTL_SECONDS = 60;
const CACHE_DISABLED_KEY = "RW.Api.Cache.Disabled";

export interface CacheOptions {
    ttlSeconds?: number;
}

export class AuthApiService {
    protected oidcService: OidcService;
    protected navigationService: NavigationService;
    private cache: SimpleLocalStorageCache

    constructor(oidcService: OidcService, navigationService: NavigationService) {
        this.oidcService = oidcService;
        this.navigationService = navigationService;
        this.cache = createCache({ namespace: 'RW.Api', defaultSecondsUntilExpiration: DEFAULT_CACHE_TTL_SECONDS });
    }

    protected authFetch = async (url: RequestInfo, request?: any, cacheOptions?: CacheOptions): Promise<any> => {
        try {
            const cacheDisabled = localStorage.getItem(CACHE_DISABLED_KEY);
            const claims = await this.oidcService.getClaims();
            const cacheKey = await this.getCacheKey(url, claims.id, request);
            const cachedResponse = cacheKey && !cacheDisabled ? await this.getCachedResponse(cacheKey) : null;

            if (cachedResponse)
                return cachedResponse;

            const authRequest = await this.addRequestHeader(request);
            const response = await fetch(url, authRequest);

            if (!response.ok) {
                const error = await this.getErrorMessage(url, response);
                return Promise.reject({ ...error });
            }

            const contentType = response.headers.get("Content-Type") ?? undefined;
            const responseText = response.status === 204 ? null : await response.text();

            if (!cacheDisabled && cacheOptions && cacheKey && contentType && contentType.includes('application/json')) {
                this.cache.set(cacheKey,
                    stringify({
                        responseText,
                        headers: response.headers,
                        status: response.status,
                        statusText: response.statusText
                    }),
                    { secondsUntilExpiration: cacheOptions.ttlSeconds || DEFAULT_CACHE_TTL_SECONDS }
                );
            }

            return new Response(responseText, {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers
            });
        } catch (error: any) {
            const responseError: ResponseError = {
                status: error.status,
                message: this.formatApiErrorMessage(url, error.message)
            };
            return Promise.reject(responseError);
        }
    };

    private getCacheKey = async (url: RequestInfo, userId?: number, request?: any) => {
        const requestHash = await createRequestHash(request);
        if (!requestHash) return null;
        return `${url.toString()}::${userId}::${requestHash}`;
    }

    private getCachedResponse = async (cacheKey: string) => {
        const cachedData = await this.cache.get(cacheKey);

        if (!cachedData) return null;

        incrementCacheUsageStats(cacheKey.split('::')[0]);

        const { responseText, headers, status, statusText } = JSON.parse(cachedData);

        return new Response(responseText, {
            headers,
            status,
            statusText
        });
    }

    private getErrorMessage = async (url: RequestInfo, response: Response) => {
        const error: ResponseError = await extractErrorFromResponse(response);

        switch (response.status) {
            case 401:
                error.message = this.formatApiErrorMessage(url, 'Unauthorized access.');
                break;
            case 403:
                error.message = this.formatApiErrorMessage(url, 'Operation is forbidden.');
                break;
            default:
                error.message = this.formatApiErrorMessage(url, 'Internal server error.');
                break;
        }

        return error;
    }

    protected addRequestHeader = async (request?: any) => {
        const accessToken = await this.oidcService.getAccessToken();
        const locationId = await this.getLocationId();
        const customerId = this.navigationService.getCustomerId();

        if (!accessToken) {
            throw new AuthApiServiceError('Unable to obtain the access token', 401);
        }

        if (!request) {
            request = {};
        }

        if (!request.headers) {
            request.headers = {};
        }

        request.headers.Authorization = 'Bearer ' + accessToken;

        if (locationId && !request.headers.LocationId) {
            request.headers.LocationId = locationId;
        }

        if (customerId) {
            request.headers.CustomerId = customerId;
        }

        return request;
    };

    private formatApiErrorMessage = (url: RequestInfo, message: string): string => {
        return `${message}. URL: ${url}`;
    };

    private getLocationId = async () => {
        const claims = await this.oidcService.getClaims();
        const locationIdUrlParameter = this.navigationService.getLocationId();
        const locationId = locationIdUrlParameter
            ? locationIdUrlParameter
            : claims!.locationId;

        return locationId;
    }

    public static deterministicJsonStringify = (obj: any): string | undefined => {
        return stringify(obj);
    }
}
